// TimetablePanel — build a class's weekly timetable grid (day x period),
// and assign a one-off substitute teacher for a specific date without
// touching the permanent weekly template.
import { useEffect, useState } from 'react';
import { getClasses, getStaff, getTimetableSlots, upsertTimetableSlot, deleteTimetableSlot, setSubstitute } from '../api/client';
import Button from '../ui/Button';

const DAYS = [
  { num: 1, label: 'Mon' }, { num: 2, label: 'Tue' }, { num: 3, label: 'Wed' },
  { num: 4, label: 'Thu' }, { num: 5, label: 'Fri' }, { num: 6, label: 'Sat' },
];
const PERIODS = [1, 2, 3, 4, 5, 6, 7, 8];

export default function TimetablePanel() {
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classId, setClassId] = useState('');
  const [slots, setSlots] = useState([]);
  const [editing, setEditing] = useState(null); // {day, period, slot|null}
  const [subForm, setSubForm] = useState(null); // {slotId, date, substitute_staff_id}
  const [msg, setMsg] = useState('');

  useEffect(() => { loadBase(); }, []);
  useEffect(() => { if (classId) loadSlots(); }, [classId]);

  async function loadBase() {
    try {
      const [c, s] = await Promise.all([getClasses(), getStaff()]);
      setClasses(c);
      setTeachers(s.filter(x => x.designation === 'Teacher'));
      if (c.length) setClassId(String(c[0].id));
    } catch { setMsg('Failed to load classes/staff'); }
  }

  async function loadSlots() {
    try { setSlots(await getTimetableSlots(classId)); } catch { setMsg('Failed to load timetable'); }
  }

  function slotFor(day, period) {
    return slots.find(s => s.day_of_week === day && s.period_number === period);
  }

  function openEditor(day, period) {
    const existing = slotFor(day, period);
    setEditing({
      day, period,
      subject: existing?.subject || '',
      staff_id: existing?.staff_id || '',
      start_time: existing?.start_time?.slice(0, 5) || '',
      end_time: existing?.end_time?.slice(0, 5) || '',
    });
  }

  async function saveSlot(e) {
    e.preventDefault();
    try {
      await upsertTimetableSlot({
        class_id: +classId, day_of_week: editing.day, period_number: editing.period,
        subject: editing.subject || null, staff_id: editing.staff_id || null,
        start_time: editing.start_time || null, end_time: editing.end_time || null,
      });
      setEditing(null);
      loadSlots();
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.error || err.message));
    }
  }

  async function removeSlot() {
    const existing = slotFor(editing.day, editing.period);
    if (!existing) { setEditing(null); return; }
    try { await deleteTimetableSlot(existing.id); setEditing(null); loadSlots(); }
    catch (err) { setMsg('Error: ' + (err.response?.data?.error || err.message)); }
  }

  async function saveSubstitute(e) {
    e.preventDefault();
    try {
      await setSubstitute(subForm.slotId, subForm.date, +subForm.substitute_staff_id);
      setMsg('Substitute assigned ✅');
      setSubForm(null);
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.error || err.message));
    }
  }

  const inputCls = "border border-line rounded-s px-3 py-2 text-sm bg-paper-raised focus:outline-none focus:ring-2 focus:ring-transit mt-1";
  const labelCls = "flex flex-col text-xs font-bold text-ink-soft";

  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-4">Timetable</h2>
      {msg && (
        <p className={`text-sm font-bold px-3 py-2 rounded-s mb-4 inline-block ${msg.includes('Error') ? 'bg-critical-tint text-critical' : 'bg-success-tint text-success'}`}>
          {msg}
        </p>
      )}

      <label className={labelCls + ' max-w-[220px]'}>Class
        <select className={inputCls} value={classId} onChange={e => setClassId(e.target.value)}>
          {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </label>

      <div className="overflow-x-auto mt-4">
        <table className="w-full text-sm min-w-[700px]">
          <thead>
            <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
              <th className="px-4 py-2.5">Period</th>
              {DAYS.map(d => <th key={d.num} className="px-4 py-2.5">{d.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {PERIODS.map(p => (
              <tr key={p}>
                <td className="px-4 py-3 border-b border-line-soft font-bold text-ink">{p}</td>
                {DAYS.map(d => {
                  const slot = slotFor(d.num, p);
                  return (
                    <td key={d.num} className="px-4 py-3 border-b border-line-soft border-l border-line-soft cursor-pointer align-top min-w-[120px] hover:bg-transit-tint/40"
                      onClick={() => openEditor(d.num, p)}>
                      {slot ? (
                        <div>
                          <div className="font-bold text-ink">{slot.subject || '—'}</div>
                          <div className="text-xs text-ink-soft">{slot.staff_name || 'Unassigned'}</div>
                          <Button
                            variant="secondary"
                            className="!px-1.5 !py-0.5 !text-[11px] mt-1"
                            onClick={(e) => { e.stopPropagation(); setSubForm({ slotId: slot.id, date: '', substitute_staff_id: '' }); }}
                          >
                            Assign substitute
                          </Button>
                        </div>
                      ) : (
                        <span className="text-line">+ Add</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-ink/40 flex items-center justify-center z-[1000]" onClick={() => setEditing(null)}>
          <form className="bg-paper-raised rounded-l shadow-2 p-6 min-w-[320px] flex flex-col gap-3" onClick={e => e.stopPropagation()} onSubmit={saveSlot}>
            <h3 className="font-display font-bold text-lg text-ink m-0">{DAYS.find(d => d.num === editing.day).label} — Period {editing.period}</h3>
            <label className={labelCls}>Subject
              <input className={inputCls} value={editing.subject} onChange={e => setEditing({ ...editing, subject: e.target.value })} placeholder="e.g. Mathematics" />
            </label>
            <label className={labelCls}>Teacher
              <select className={inputCls} value={editing.staff_id} onChange={e => setEditing({ ...editing, staff_id: e.target.value })}>
                <option value="">— Free period —</option>
                {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
            <div className="flex gap-2">
              <label className={labelCls + ' flex-1'}>Start
                <input type="time" className={inputCls} value={editing.start_time} onChange={e => setEditing({ ...editing, start_time: e.target.value })} />
              </label>
              <label className={labelCls + ' flex-1'}>End
                <input type="time" className={inputCls} value={editing.end_time} onChange={e => setEditing({ ...editing, end_time: e.target.value })} />
              </label>
            </div>
            <div className="flex gap-2 mt-1">
              <Button type="submit">Save</Button>
              <Button type="button" variant="danger" onClick={removeSlot}>Delete Slot</Button>
              <Button type="button" variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      {subForm && (
        <div className="fixed inset-0 bg-ink/40 flex items-center justify-center z-[1000]" onClick={() => setSubForm(null)}>
          <form className="bg-paper-raised rounded-l shadow-2 p-6 min-w-[320px] flex flex-col gap-3" onClick={e => e.stopPropagation()} onSubmit={saveSubstitute}>
            <h3 className="font-display font-bold text-lg text-ink m-0">Assign Substitute Teacher</h3>
            <label className={labelCls}>Date
              <input type="date" required className={inputCls} value={subForm.date} onChange={e => setSubForm({ ...subForm, date: e.target.value })} />
            </label>
            <label className={labelCls}>Substitute Teacher
              <select required className={inputCls} value={subForm.substitute_staff_id} onChange={e => setSubForm({ ...subForm, substitute_staff_id: e.target.value })}>
                <option value="">— Select —</option>
                {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
            <div className="flex gap-2 mt-1">
              <Button type="submit">Assign</Button>
              <Button type="button" variant="secondary" onClick={() => setSubForm(null)}>Cancel</Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
